/**
 * Navigation composition.
 *
 * Navigation is DERIVED, never declared as a static tree. Items come from module
 * manifests and are filtered by the resolved workspace's capabilities and the
 * user's permissions.
 *
 * This is the mechanism behind "a salon employee should not see dozens of
 * irrelevant inventory modules". There is no per-role navigation list anywhere
 * in the codebase to fall out of date — there is one registry and one filter.
 *
 * No `vertical === "bakery"` conditional appears here, or anywhere else. If a
 * business check needs to decide whether a destination is offered, it goes
 * through a capability id, which the server resolves.
 *
 * ---------------------------------------------------------------------------
 * TWO AUTHORITY AXES, NAMED EXPLICITLY
 * ---------------------------------------------------------------------------
 * The API resolves TWO independent kinds of authority for an actor, and a
 * destination may require either:
 *
 *   - `tenant` — the effective permissions of the actor's membership role in the
 *     selected tenant (`catalog.offer.read`, `inventory.stock.read`, …).
 *   - `platform` — the actor's live platform grants on this deployment
 *     (`tenant.read`, `tenant.create`, …). Platform authority is deployment-local
 *     and lives outside tenant RBAC (ADR-0004 Amendment 1).
 *
 * A destination states which axis carries its requirement. Merging the two into
 * one list would offer the platform's merchant list to a tenant administrator, and
 * hide it from a platform administrator — the two are different people with
 * different powers.
 *
 * ---------------------------------------------------------------------------
 * THE PERMISSION CODES ARE THE API'S
 * ---------------------------------------------------------------------------
 * Every code below is a code the API actually issues. The admin does not invent
 * codes, and a code the API never issues gates nothing — which is the safe
 * direction: an unknown code hides a destination rather than exposing one.
 *
 * ---------------------------------------------------------------------------
 * THE CAPABILITY IDS ARE THE SERVER'S, TOO
 * ---------------------------------------------------------------------------
 * A `capability` below is an ENTITLEMENT capability id read from
 * `GET /api/v1/foundation/entitlements` — the ids the server actually holds for
 * the tenant. Two are used, and both are literal:
 *
 *   - `retail_basic` — the merchant's retail PLAN entitlement (M36's plan key).
 *   - `basic_queue` — the queue capability (M64's `basicQueue`, in M05's
 *     snake_case storage form).
 *
 * These are not area names invented by the console. They are the vocabulary the
 * entitlement table holds, and the demo proves the difference is real: the
 * `demo-services` tenant holds `retail_basic` but NOT `basic_queue`, so its
 * owner is offered no Appointments screen while the `demo-products` owner is.
 *
 * The API does not enforce these entitlements on the reads below — the server
 * authorizes those by permission alone. The gate is therefore a CONSOLE
 * affordance: it keeps a screen the tenant has not bought out of the sidebar.
 * It never grants anything, and a screen reached by typing its URL is still
 * refused by the server.
 */
import { checkPermissions } from '@/shared/permissions';
import type { PermissionSpec } from '@/shared/permissions';

export type NavItem = {
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly group: string;
  readonly order: number;
  /** Capability that must be present for this destination to exist. */
  readonly capability?: string;
  /** Permission required. Absent means any signed-in user may see it. */
  readonly permission?: PermissionSpec;
  /**
   * Which authority axis carries `permission`.
   *
   * Defaults to `tenant`, because almost every destination in this console is a
   * tenant's own screen. The platform destinations say so explicitly.
   */
  readonly permissionScope?: 'tenant' | 'platform';
  readonly icon?: string;
  /** Whether the destination appears in the mobile bottom navigation. */
  readonly mobile?: boolean;
};

/**
 * The allowlisted module registry.
 *
 * Build-time and static. No runtime module imports and no tenant-generated
 * entries: a destination that is not in this list cannot be navigated to.
 */
export const MODULE_NAVIGATION: ReadonlyArray<NavItem> = [
  {
    id: 'overview',
    to: '/overview',
    label: 'Dashboard',
    group: 'General',
    order: 10,
    icon: 'dashboard',
  },
  {
    id: 'platform.tenants',
    to: '/platform/tenants',
    label: 'Tenants',
    group: 'Platform',
    order: 20,
    icon: 'users',
    // PLATFORM authority: the merchant registry of this deployment. A tenant
    // administrator holds no platform grant and therefore never sees this.
    permissionScope: 'platform',
    permission: { require: ['tenant.read'] },
  },
  {
    id: 'catalog.products',
    to: '/catalog/products',
    label: 'Products',
    group: 'Catalog',
    order: 30,
    icon: 'package',
    // The CATALOGUE read is public scope in the API (`catalog.product.read` gates
    // its WRITES, not its reads), so no tenant permission gates the screen. What
    // the tenant has BOUGHT does: a merchant without the retail plan is offered
    // no catalogue at all.
    capability: 'retail_basic',
  },
  {
    id: 'catalog.setup',
    to: '/catalog/setup',
    label: 'Catalogue setup',
    group: 'Catalog',
    order: 31,
    icon: 'sliders',
    // The AI-ASSISTED way to start a catalogue: the assistant proposes a
    // category tree and its attributes, and everything it proposes is saved
    // through the ordinary catalogue API. The tenant's retail plan gates it,
    // like the other catalogue entries.
    capability: 'retail_basic',
  },
  {
    id: 'catalog.media',
    to: '/catalog/media',
    label: 'Media',
    group: 'Catalog',
    order: 32,
    icon: 'image',
    // A CONTENT surface: M44 authorizes it by permission alone, so the console
    // gates it by permission alone too. No capability id is invented for it.
    permission: { require: ['content.mediaasset.read'] },
  },
  {
    id: 'inventory.stock',
    to: '/inventory/stock',
    label: 'Stock',
    group: 'Inventory',
    order: 35,
    icon: 'warehouse',
    // TENANT authority: the tenant's own stock balances and movements.
    capability: 'retail_basic',
    permission: { require: ['inventory.stock.read'] },
  },
  {
    id: 'bookings.appointments',
    to: '/bookings/appointments',
    label: 'Appointments',
    group: 'Scheduling',
    order: 40,
    icon: 'calendar',
    // The queue capability, plus the tenant permission that reads bookings.
    capability: 'basic_queue',
    permission: { require: ['bookings.booking.read'] },
  },
  {
    id: 'settings.workspace',
    to: '/settings/workspace',
    label: 'Workspace settings',
    group: 'Settings',
    order: 50,
    icon: 'settings',
    permission: { require: ['catalog.offer.update'] },
  },
  {
    id: 'platform.ai',
    to: '/platform/ai',
    label: 'AI console',
    group: 'Platform',
    order: 25,
    icon: 'sliders',
    // PLATFORM authority: the deployment's AI providers and its defaults. A
    // tenant administrator holds no platform grant and therefore never sees
    // this.
    permissionScope: 'platform',
    permission: { require: ['ai.provider.platform.read'] },
  },
  {
    id: 'platform.ai.chat',
    to: '/platform/ai/chat',
    label: 'AI chat',
    group: 'Platform',
    order: 26,
    icon: 'messageSquare',
    // PLATFORM authority: the deployment's AI chat surface. A tenant
    // administrator holds no platform grant and therefore never sees this.
    permissionScope: 'platform',
    permission: { require: ['ai.provider.platform.read'] },
  },
  {
    id: 'tenant.ai',
    to: '/tenant/$tenantId/workspace/ai',
    label: 'AI settings',
    group: 'Settings',
    order: 52,
    icon: 'sliders',
    // TENANT authority: how AI behaves for ONE tenant's workspace. The path
    // carries the tenant as a parameter, in the same shape as the workspace
    // route itself; the API resolves the tenant from the caller's session, so
    // the segment names the tenant rather than selecting it.
    permission: { require: ['ai.settings.read'] },
  },
  {
    id: 'agreements.obligations',
    to: '/agreements',
    label: 'Agreements',
    group: 'Workspace',
    order: 60,
    icon: 'fileText',
    // TENANT authority: the agreements THIS workspace is bound by, and the
    // signing surface for them. A member who cannot sign still sees the list —
    // the API decides what they may do with each entry.
    permission: { require: ['agreement.read'] },
  },
  {
    id: 'platform.agreements',
    to: '/platform/agreements/documents',
    label: 'Agreements',
    group: 'Platform',
    order: 30,
    icon: 'fileText',
    // PLATFORM authority: authoring the documents and requirements that bind
    // tenants. A tenant administrator holds no platform grant and therefore
    // never sees this.
    permissionScope: 'platform',
    permission: { require: ['platform.agreement.document.read'] },
  },
];

export type NavigationContext = {
  /** The actor's effective TENANT-scoped permissions. */
  readonly permissions: ReadonlyArray<string>;
  /** The actor's live PLATFORM-scoped grants. */
  readonly platformPermissions: ReadonlyArray<string>;
  readonly capabilities: ReadonlyArray<string>;
};

/**
 * Filters the registry down to what this user may actually reach.
 *
 * An item with a capability the scope lacks is GONE, not disabled. A disabled
 * destination still advertises that the feature exists; omitting it entirely is
 * what keeps a salon manager's sidebar readable.
 *
 * The permission is checked on the axis the item declares. An item that declares
 * neither is offered to every signed-in user — the API re-authorizes every call
 * regardless, so this filter is about what the CONSOLE shows, never about what
 * the server permits.
 */
export function composeNavigation(context: NavigationContext): ReadonlyArray<NavItem> {
  return MODULE_NAVIGATION.filter((item) => {
    if (item.capability && !context.capabilities.includes(item.capability)) return false;
    if (item.permission) {
      const axis = item.permissionScope === 'platform' ? context.platformPermissions : context.permissions;
      if (!checkPermissions(axis, item.permission)) return false;
    }
    return true;
  }).sort((a, b) => a.order - b.order);
}

/** Groups composed items for rendering, preserving the composed order. */
export function groupNavigation(items: ReadonlyArray<NavItem>): ReadonlyArray<{
  group: string;
  items: ReadonlyArray<NavItem>;
}> {
  const groups = new Map<string, NavItem[]>();

  for (const item of items) {
    const bucket = groups.get(item.group);
    if (bucket) bucket.push(item);
    else groups.set(item.group, [item]);
  }

  return [...groups.entries()].map(([group, groupItems]) => ({ group, items: groupItems }));
}
